import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, inject, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule} from '@angular/forms';
import { OffersService } from './offers.service';
import { DerivedOfferStatus, Offer } from './offers.model';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, tap } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OffersModalComponent } from './offers-modal/offers-modal.component';
import { ToastrService } from 'ngx-toastr';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { PaginationComponent } from '../Common/pagination/pagination.component';

/**
 * Catalog of offer types shown in the filter dropdown. Kept in sync with
 * the backend OfferType enum — if a new type is added there, append it
 * here too. Label is the human-readable form used in the dropdown.
 */
const OFFER_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'PriceOffOnProduct', label: 'Price Off On Product' },
  { value: 'PercentOffOnMRP', label: 'Percent Off On MRP' },
  { value: 'BuyXGetYFree', label: 'Buy X Get Y Free' },
  { value: 'BuyXYGetZSpecialPrice', label: 'Buy X+Y Get Z Special Price' },
  { value: 'BuyXYGetZFree', label: 'Buy X+Y Get Z Free' },
  { value: 'BuyXYGetDiscountOnX', label: 'Buy XY — Item % Discount' },
  { value: 'BuyXYGetAmountDiscountOnX', label: 'Buy XY — Item Amount Off' },
  { value: 'BillRangeGetPercentOff', label: 'Bill Range — Percent Off' },
  { value: 'BillRangeGetAmountOff', label: 'Bill Range — Amount Off' },
  { value: 'BillRangeGetFreeItem', label: 'Bill Range — Free Item' },
  { value: 'BillRangeGetFreeSet', label: 'Bill Range — Free Set' },
  { value: 'BillRangeGetScratchCard', label: 'Bill Range — Scratch Card' },
  { value: 'CategoryPercentDiscount', label: 'Category Percent Discount' },
  { value: 'DepartmentPercentDiscount', label: 'Department Percent Discount' },
  { value: 'BrandPercentDiscount', label: 'Brand Percent Discount' },
  { value: 'AllProductsDiscount', label: 'All Products Discount' },
  { value: 'HappyHourDiscount', label: 'Happy Hour Discount' },
  { value: 'ProgressiveDiscount', label: 'Progressive Discount' },
  { value: 'ExclusionBasedDiscount', label: 'Exclusion-Based Discount' },
];

@Component({
  selector: 'app-offers',
  imports: [ FormsModule, DatePipe, DecimalPipe, RouterModule, NgSelectModule, PaginationComponent ],
  templateUrl: './offers.component.html',
  styleUrl: './offers.component.scss'
})
export class OffersComponent implements OnInit {
  offers: Offer[] = [];
  offerSearchTerm: string = '';
  currentPage: number = 1;
  pageSize: number = 10;
  total: number = 0;
  selectedOffer: Offer | null = null;

  offerToDelete: Offer | null = null;
  pageCount: number = 0;
  private offersService = inject(OffersService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);


  dropdownOpen = false;
  selectedProduct: any = null;

  offerSearchInput$ = new Subject<string>();
  offersLoading = false;

  // ── Filter state ────────────────────────────────────────────────────
  // All filters are optional and combine with AND semantics on the
  // backend. Empty values are stripped before the request so the server
  // can short-circuit each filter cleanly.
  readonly offerTypeOptions = OFFER_TYPE_OPTIONS;
  readonly statusOptions: Array<{ value: DerivedOfferStatus; label: string }> = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'PAUSED', label: 'Paused' },
    { value: 'SCHEDULED', label: 'Scheduled' },
    { value: 'EXPIRED', label: 'Expired' },
  ];
  stores: Array<{ id: string; name: string }> = [];
  storesLoading = false;
  selectedStoreIds: string[] = [];
  // Default to ACTIVE so the page lands on the offers an admin actually
  // cares about day-to-day (what's running at POS right now). Expired
  // and scheduled offers are still one filter-click away — but they're
  // not the noise we want to greet a fresh page load with.
  selectedStatus: DerivedOfferStatus | null = 'ACTIVE';
  selectedOfferType: string | null = null;
  filterStartDate: string = '';
  filterEndDate: string = '';
  filtersExpanded = false;

  // ── MRP-off-list upload ─────────────────────────────────────────────
  // One sheet → N PriceOffOnProduct offers (one per "Offer Price - ..."
  // column), each line carrying its own offer price. Two-step: preview
  // (dry run, shows errors + what will be created) then confirm.
  @ViewChild('mrpOffUploadModal') mrpOffUploadModalRef!: TemplateRef<any>;
  upload = this.freshUploadState();

  /** Offer types the sheet upload can produce (per-SKU value column). */
  readonly uploadOfferTypes: Array<{ value: 'PriceOffOnProduct' | 'PercentOffOnMRP'; label: string; valueLabel: string; hint: string }> = [
    {
      value: 'PriceOffOnProduct',
      label: 'Price Off On Product — offer price per SKU',
      valueLabel: 'Offer Price',
      hint: 'Each SKU sells at the listed offer price (must be ≤ MRP).',
    },
    {
      value: 'PercentOffOnMRP',
      label: 'Percent Off On MRP — discount % per SKU',
      valueLabel: 'Discount %',
      hint: 'Each SKU sells at MRP less the listed percent (e.g. 10 = 10% off).',
    },
  ];

  get uploadTypeMeta() {
    return this.uploadOfferTypes.find((t) => t.value === this.upload.offerType) ?? this.uploadOfferTypes[0];
  }

  private freshUploadState() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      offerType: 'PriceOffOnProduct' as 'PriceOffOnProduct' | 'PercentOffOnMRP',
      offerName: '',
      description: '',
      startDate: today,
      endDate: today,
      isActive: true,
      storeIds: [] as string[],
      priority: 1,
      file: null as File | null,
      fileName: '',
      preview: null as any,
      previewing: false,
      committing: false,
    };
  }

  openMrpOffUpload() {
    this.upload = this.freshUploadState();
    this.modalService.open(this.mrpOffUploadModalRef, {
      size: 'xl',
      centered: true,
      scrollable: true,
      backdrop: 'static',
    });
  }

  onUploadFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (file && !/\.xlsx?$/i.test(file.name)) {
      this.toastr.error('Invalid file format. Please upload an Excel (.xlsx) file.');
      input.value = '';
      return;
    }
    this.upload.file = file;
    this.upload.fileName = file?.name ?? '';
    this.upload.preview = null;
  }

  /** Any header change invalidates the dry run — force a fresh preview. */
  onUploadFieldChange() {
    this.upload.preview = null;
  }

  get canPreviewUpload(): boolean {
    const u = this.upload;
    return !!(u.file && u.offerName.trim() && u.startDate && u.endDate)
      && !u.previewing && !u.committing;
  }

  get canCommitUpload(): boolean {
    const p = this.upload.preview;
    return !!p && !(p.errors?.length) && (p.offers?.length ?? 0) > 0
      && !this.upload.committing && !this.upload.previewing;
  }

  private buildUploadForm(): FormData {
    const u = this.upload;
    const fd = new FormData();
    fd.append('file', u.file as File);
    fd.append('offerName', u.offerName.trim());
    fd.append('description', u.description?.trim() ?? '');
    fd.append('startDate', u.startDate);
    fd.append('endDate', u.endDate);
    fd.append('isActive', String(!!u.isActive));
    fd.append('offerType', u.offerType);
    fd.append('storeIds', (u.storeIds ?? []).join(','));
    fd.append('priority', String(u.priority ?? 1));
    return fd;
  }

  previewMrpOffUpload() {
    if (!this.canPreviewUpload) return;
    this.upload.previewing = true;
    this.offersService.previewMrpOffList(this.buildUploadForm()).subscribe({
      next: (res) => {
        this.upload.previewing = false;
        this.upload.preview = res;
      },
      error: (err) => {
        this.upload.previewing = false;
        this.toastr.error(err?.error?.message || 'Could not read the file.');
      },
    });
  }

  confirmMrpOffUpload(modal: any) {
    if (!this.canCommitUpload) return;
    this.upload.committing = true;
    this.offersService.uploadMrpOffList(this.buildUploadForm()).subscribe({
      next: (res) => {
        this.upload.committing = false;
        modal.close();
        const n = res?.created?.length ?? 0;
        this.toastr.success(`Created ${n} offer${n === 1 ? '' : 's'} from the MRP off list.`);
        this.loadOffers();
      },
      error: (err) => {
        this.upload.committing = false;
        // Server re-validates on commit; if the file changed under us the
        // row errors come back here — show them in place of the stale preview.
        if (err?.status === 400 && err?.error?.preview) {
          this.upload.preview = err.error.preview;
        }
        this.toastr.error(err?.error?.message || 'Upload failed. Nothing was created.');
      },
    });
  }

  downloadMrpOffTemplate() {
    const type = this.upload.offerType;
    this.offersService.downloadMrpOffListTemplate(type).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = type === 'PercentOffOnMRP' ? 'mrp_percent_off_list_template.xlsx' : 'mrp_off_list_template.xlsx';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.toastr.error('Failed to download the template.'),
    });
  }

  ngOnInit() {
    this.loadOffers();
    this.offerSearch();
    this.loadStores();
  }

  /** Lazy-load the store list once for the filter dropdown. Reuses the
   *  same endpoint the offer modal uses so admin sees a consistent
   *  store catalog across the offer feature. */
  loadStores() {
    this.storesLoading = true;
    this.offersService.getStores().subscribe({
      next: (res: any) => {
        // Some APIs return { data: [...] }, others return the array
        // directly — normalise so the template only deals with an array.
        this.stores = Array.isArray(res) ? res : (res?.data ?? []);
        this.storesLoading = false;
      },
      error: () => { this.storesLoading = false; },
    });
  }

  /** Build the filter payload sent to the service. Empty fields are
   *  intentionally omitted so the backend can keep its where-clause
   *  minimal (and the URL stays readable in dev tools). */
  private currentFilters() {
    return {
      storeIds: this.selectedStoreIds?.length ? this.selectedStoreIds : undefined,
      status: this.selectedStatus ?? undefined,
      type: this.selectedOfferType ?? undefined,
      startDate: this.filterStartDate || undefined,
      endDate: this.filterEndDate || undefined,
    };
  }

  /** Any filter applied? Used to show a "Clear filters" affordance and
   *  to display the active-filter count badge next to the toggle. */
  get activeFilterCount(): number {
    let n = 0;
    if (this.selectedStoreIds?.length) n++;
    if (this.selectedStatus) n++;
    if (this.selectedOfferType) n++;
    if (this.filterStartDate || this.filterEndDate) n++;
    return n;
  }

  /** Apply current filter selections — resets to page 1 and reloads. */
  applyFilters() {
    this.currentPage = 1;
    this.loadOffers();
  }

  /** Reset all filter fields (but not the search term — that has its
   *  own input). Reload the list so the table reflects the cleared
   *  state immediately. */
  clearFilters() {
    this.selectedStoreIds = [];
    this.selectedStatus = null;
    this.selectedOfferType = null;
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.currentPage = 1;
    this.loadOffers();
  }

  toggleFilters() {
    this.filtersExpanded = !this.filtersExpanded;
  }

  onSearch() {
    this.offerSearchInput$.next(this.offerSearchTerm);
  }

  loadOffers() {
    this.offersLoading = true;
    this.offersService
    .getOffers({page: this.currentPage, limit: this.pageSize}, this.offerSearchTerm, this.currentFilters())
    .subscribe({
      next: (response: any) => {
        this.offers = response.data;
        this.total = response.total;
        this.pageCount = response.pageCount;
        this.offersLoading = false;
      },
      error: () => { this.offersLoading = false; },
    });
  }

  /**
   * Bill-level offer types — these all compete with each other at billing
   * time. Used to detect overlapping offers in the list so admins can
   * see at a glance which offers might be in the same "only one wins"
   * resolution group.
   */
  private static readonly BILL_OFFER_TYPES = new Set([
    'BillRangeGetPercentOff',
    'BillRangeGetAmountOff',
    'BillRangeGetFreeItem',
    'BillRangeGetFreeSet',
  ]);

  /**
   * Compute the display status for an offer from its dates + isActive
   * flag. This is the single source of truth for the badge — backend
   * stores only isActive, everything else is deduced here so the UI
   * doesn't depend on a midnight cron flipping fields. EXPIRED beats
   * PAUSED beats SCHEDULED beats ACTIVE.
   */
  derivedStatus(offer: Offer): DerivedOfferStatus {
    const today = this.istDayNum(new Date());
    const start = offer.startDate ? this.istDayNum(new Date(offer.startDate)) : NaN;
    const end = offer.endDate ? this.istDayNum(new Date(offer.endDate)) : NaN;
    if (!isNaN(end) && end < today) return 'EXPIRED';
    if (!isNaN(start) && start > today) return 'SCHEDULED';
    return offer.isActive ? 'ACTIVE' : 'PAUSED';
  }

  /**
   * Calendar-day number (YYYYMMDD) of an instant on the IST wall clock.
   * Offers store start/end as the UTC-midnight of a calendar day, so a raw
   * timestamp comparison marks an offer ending "today" expired from 05:30
   * IST. Comparing IST day numbers keeps the window an inclusive calendar
   * check — valid for the whole IST day on both start and end dates.
   */
  private istDayNum(d: Date): number {
    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
    const ist = new Date(d.getTime() + IST_OFFSET_MS);
    return ist.getUTCFullYear() * 10000 + (ist.getUTCMonth() + 1) * 100 + ist.getUTCDate();
  }

  /** Title attribute / tooltip text per derived status. */
  statusTooltip(offer: Offer): string {
    switch (this.derivedStatus(offer)) {
      case 'EXPIRED': return 'End date is in the past — offer no longer applies at POS.';
      case 'SCHEDULED': return 'Start date is in the future — offer will activate automatically when the window opens.';
      case 'ACTIVE': return 'Click to pause this offer.';
      case 'PAUSED': return 'Click to activate this offer.';
    }
  }

  /** Returns true if the toggle pill should be clickable. EXPIRED and
   *  SCHEDULED offers can't be toggled — admins must edit the date
   *  range first. */
  canToggle(offer: Offer): boolean {
    const s = this.derivedStatus(offer);
    return s === 'ACTIVE' || s === 'PAUSED';
  }

  /**
   * One-click pause / resume from the list page — sends a partial
   * PATCH that only carries isActive (other fields untouched). Uses
   * the same endpoint as the modal so backend validation, audit, and
   * sync semantics stay consistent. On success we optimistically
   * patch the local row so the badge flips immediately without a
   * full list reload — perceived latency drops from ~500ms to ~0.
   * Guarded by canToggle() — clicking on an EXPIRED/SCHEDULED pill
   * is a no-op so admins can't accidentally PATCH an offer whose
   * dates make the toggle meaningless.
   */
  toggleActive(offer: Offer, evt: Event) {
    evt.stopPropagation();
    if (!offer?.id) return;
    if (!this.canToggle(offer)) return;
    // Pausing an offer stops it at every till on the next sync, and
    // activating one starts charging customers less — neither should
    // happen on a stray click. Confirm first; confirmToggle() does the PATCH.
    this.offerToToggle = offer;
    this.modalService.open(this.toggleStatusModalRef, {
      ariaLabelledBy: 'toggleStatusModalLabel',
      centered: true,
    });
  }

  /** The offer whose status pill was clicked, pending confirmation. */
  offerToToggle: Offer | null = null;
  toggleInProgress = false;
  @ViewChild('toggleStatusModal') toggleStatusModalRef!: TemplateRef<any>;

  /** True when confirming would PAUSE the pending offer (it is active now). */
  get toggleWillPause(): boolean {
    return !!(this.offerToToggle as any)?.isActive;
  }

  confirmToggle(modal: any) {
    const offer = this.offerToToggle;
    if (!offer?.id || this.toggleInProgress) return;
    const next = !(offer as any).isActive;
    this.toggleInProgress = true;
    this.offersService.updateOffer(offer.id, { isActive: next } as any).subscribe({
      next: () => {
        (offer as any).isActive = next;
        this.toggleInProgress = false;
        this.offerToToggle = null;
        modal.close();
        this.toastr.success(next ? 'Offer activated' : 'Offer paused');
      },
      error: () => {
        this.toggleInProgress = false;
        this.toastr.error('Could not change status');
      },
    });
  }

  /**
   * Count how many OTHER bill-level offers on the current page overlap
   * with this one — same offer type family and overlapping date window.
   * If >0, the offers list shows a "competes with N" tag so the admin
   * understands why this offer might not always be the one that applies
   * at the POS.
   *
   * Caveat: this only checks the loaded page (10-40 rows), not the full
   * catalog. Conflicts across pages are not detected here. Good enough
   * for daily admin workflow; full-catalog conflict detection would
   * need a dedicated backend endpoint.
   */
  competingOfferCount(offer: Offer): number {
    if (!OffersComponent.BILL_OFFER_TYPES.has(offer.offerType as string)) return 0;
    const myStart = new Date(offer.startDate as any).getTime();
    const myEnd = new Date(offer.endDate as any).getTime();
    return this.offers.filter((other) => {
      if (other.id === offer.id) return false;
      if (!OffersComponent.BILL_OFFER_TYPES.has(other.offerType as string)) return false;
      const oStart = new Date(other.startDate as any).getTime();
      const oEnd = new Date(other.endDate as any).getTime();
      // Two ranges overlap iff each starts before the other ends.
      return myStart <= oEnd && oStart <= myEnd;
    }).length;
  }

  offerSearch() {
    this.offerSearchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap((term) => {
        this.currentPage = 1;
        this.offerSearchTerm = term;
        this.offers = [];
        this.offersLoading = true;
      }),
      switchMap(term =>
        this.offersService
    .getOffers({page: this.currentPage, limit: this.pageSize}, term, this.currentFilters()).pipe(
          catchError(() => of({ data: [], hasMore: false })),
          tap(() => this.offersLoading = false)
        )
      )
    ).subscribe((response: any) => {
      this.offers = response.data;
      this.total = response.total;
      this.pageCount = response.pageCount;
    });
  }

  openAddOfferDialog(offerData?: Offer) {
        const modalRef = this.modalService.open(OffersModalComponent, {
          // Larger modal — the offer form now has multi-row free-items
          // tables and a store multi-picker that don't fit comfortably in
          // the default sm/md width.
          size: 'lg',
          // Static backdrop = clicking outside doesn't dismiss. Without
          // this an admin can lose a half-filled form by clicking the
          // greyed-out background, which is a real-world pain point for
          // long forms like this one.
          backdrop: 'static',
          // Keyboard true (default) keeps Esc as the dismiss path —
          // intentional, so the admin always has a one-key escape.
        });
        modalRef.componentInstance.data = offerData || {};
        modalRef.result
        .then(() => {
            // Promise resolves only on a successful save (modal.close()
            // in the child). Refresh the list so the new/edited row
            // becomes visible immediately.
            this.loadOffers();
        })
        .catch((reason) => {
          // Modal dismissed via activeModal.dismiss(...) — Cancel / X /
          // Esc. No list reload needed; nothing changed server-side.
          console.log('Modal dismissed with:', reason);
        });
    }

  onPageSizeChange(limit: number) {
    this.pageSize = limit;
    this.pageSizeChange();
  }

  pageSizeChange() {
    this.currentPage = 1;
    this.loadOffers();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pageCount) return;
    this.currentPage = page;
    this.loadOffers();
  }

  setOfferToDelete(offer: any) {
    if (offer === undefined) return;

    this.offerToDelete = offer;
  }

  // Open the modal for confirmation
  openDeleteConfirmationModal(content: any, offer: Offer) {
    this.offerToDelete = offer;
    this.modalService.open(content, { ariaLabelledBy: 'confirmDeleteModalLabel' });
  }

  // Close the modal manually
  closeModal() {
    this.modalService.dismissAll();
  }

  confirmDelete() {
    if (this.offerToDelete !== null) {
      this.offersService.deleteOffer(this.offerToDelete.id!).subscribe({
        next: () => {
          this.toastr.success('Offer deleted successfully');
          this.loadOffers();
          this.closeModal();
        },
        error: (err) => {
          // Backend now explains refusals (e.g. still referenced) — show it.
          this.toastr.error(err?.error?.message || 'Offer deleting failed!');
        }
      });
    }
  }
}
